import React, { ReactNode } from 'react';
import { t } from '@lingui/core/macro';
import CollapsableTreeSelect from 'src/components/CollapsableTreeSelect';
import Field, { describedById } from './Field';
import { OrgUnitItem, useOrgUnits } from './lookups';
import { FieldWidth } from './types';

export type OrgUnitSelectProps = {
  id: string;
  label: ReactNode;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  required?: boolean;
  error?: string;
  help?: ReactNode;
  width?: FieldWidth;
  disabled?: boolean;
  /** Pick from these instead of the workable org units (which are then not fetched). */
  units?: OrgUnitItem[];
  unitsLoading?: boolean;
  /** Type names whose units may be picked. Never pass an empty array: the tree treats it as "any type". */
  selectableTypes?: string[];
  infoBadge?: { path: string; colors?: Record<string, string> };
};

/**
 * Org-unit picker using the app's collapsable tree select (the same control the JSON forms used for
 * org units), collapsed below the first level, with search. `id` lands on the wrapper so tests and
 * error links can find the field; the tree select renders its own combobox.
 */
export function OrgUnitSelect({
  id,
  label,
  value,
  onChange,
  required,
  error,
  help,
  width = 'full',
  disabled,
  units,
  unitsLoading,
  selectableTypes,
  infoBadge,
}: OrgUnitSelectProps) {
  const workable = useOrgUnits(!units);
  const options = units ?? workable.options;
  const loading = units ? !!unitsLoading : workable.loading;

  return (
    <Field label={label} htmlFor={id} required={required} help={help} error={error} width={width}>
      <div id={id} aria-invalid={!!error} aria-describedby={describedById(id, error, help)}>
        <CollapsableTreeSelect
          options={options}
          id="id"
          label="name"
          parentId="parentId"
          value={value ?? null}
          defaultCollapseLevel={1}
          placeholder={loading ? t`Loading…` : t`Select…`}
          disabled={disabled || loading}
          selectableTypes={selectableTypes}
          infoBadge={infoBadge}
          onChange={(event: { target: { value: unknown } }) => {
            const next = event?.target?.value;
            onChange(next === null || next === undefined || next === '' ? null : Number(next));
          }}
        />
      </div>
    </Field>
  );
}

export default OrgUnitSelect;
